const CONFIG = {
  MODAL_CLOSE_DELAY_MS: 100,
  THUMBNAIL_WIDTH_PX: 200,
  MAX_VARIANT_TEXT_LENGTH: 100,
  CENTS_PER_EURO: 100,
  PERCENT_TO_DECIMAL: 100,
};

class BundleSummary extends HTMLElement {
  constructor() {
    super();

    if (!window._bundleSummaryListenerRegistered) {
      this._isFirstInstance = true;
    } else {
      this._isFirstInstance = false;
    }
  }

  connectedCallback() {
    this.instanceId = this.dataset.blockId;
    this.updateConfig();

    if (this._isFirstInstance) {
      this.setupGlobalUpsellListener();
    }

    this.restoreUpsellSelection();

    // Always update upsell state to catch pre-selected (highlighted) items
    this.updateUpsellState();

    if (!this.hasRendered) {
      this.updateSummary();
    }

    this.hasRendered = false;
  }

  /**
   * Read config from data attributes (simple, no state management)
   */
  updateConfig() {
    this.config = {
      mainProductId: this.dataset.mainProductId,
      blockId: this.instanceId,
      formId: this.dataset.formId,
      showFakeDiscounts: this.dataset.showFakeDiscounts === "true",
      discountRules: this.parseDiscountRules(this.dataset.discountRules),
      showItemList: this.dataset.showItemList === "true",
      showItemPrices: this.dataset.showItemPrices === "true",
      title: this.dataset.title,
      badgeText: this.dataset.badgeText || "WINTER SALE DEAL",
      styleVariant: this.dataset.styleVariant || "default",
    };

    this.selectedUpsells = [];
  }

  /**
   * Parse discount rules from string format
   * Example: "bundle-kissen:50, bundle-decken:30" → { "bundle-kissen": 50, "bundle-decken": 30 }
   */
  parseDiscountRules(rulesString) {
    const rules = {};
    if (!rulesString) return rules;

    rulesString.split(",").forEach((rule) => {
      const [tag, discount] = rule.trim().split(":");
      if (tag && discount) {
        rules[tag.trim()] = parseFloat(discount);
      }
    });

    return rules;
  }

  /**
   * Get current main product data directly from data attributes
   * Always fresh, no state management needed
   */
  getMainProductData() {
    return {
      title: this.dataset.mainProductTitle || "Hauptprodukt",
      customTitle:
        this.dataset.mainProductCustomTitle ||
        this.dataset.mainProductTitle ||
        "Hauptprodukt",
      featured_image: this.dataset.mainProductImage || "",
      variant: {
        id: this.dataset.mainProductId,
        price: parseInt(this.dataset.mainPrice) || 0,
        compareAtPrice: this.dataset.mainComparePrice
          ? parseInt(this.dataset.mainComparePrice)
          : null,
        options: this.dataset.mainVariantOptions || "",
      },
    };
  }

  /**
   * Setup global upsell change listener (once per page load)
   */
  setupGlobalUpsellListener() {
    document.addEventListener("change", (event) => {
      const target = event.target;

      if (target.matches('input[name="upsell"]')) {
        document.querySelectorAll("kc-bundle-summary").forEach((summary) => {
          if (summary.updateUpsellState) {
            summary.updateUpsellState();
          }
        });
      }
    });

    document.addEventListener("click", (event) => {
      const closeButton = event.target.closest('button[is="close-button"]');

      if (closeButton) {
        const modal = closeButton.closest("x-drawer");
        const isUpsellModal = modal?.querySelector("upsell-variant-selector");

        if (isUpsellModal) {
          setTimeout(() => {
            document
              .querySelectorAll("kc-bundle-summary")
              .forEach((summary) => {
                if (summary.updateUpsellState) {
                  summary.updateUpsellState();
                }
              });
          }, CONFIG.MODAL_CLOSE_DELAY_MS);
        }
      }
    });

    window._bundleSummaryListenerRegistered = true;
  }

  /**
   * Save COMPLETE upsell data (not just handles)
   * Use blockId for unique storage key (Desktop vs Mobile)
   */
  saveUpsellSelection() {
    try {
      const storageKey = `bundle-summary-${this.config.blockId}`;
      const upsellData = this.selectedUpsells.map((u) => ({
        handle: u.handle,
        variantId: u.variantId,
        title: u.title,
        price: u.price,
        originalPrice: u.originalPrice,
        compareAtPrice: u.compareAtPrice,
        discount: u.discount,
        image: u.image,
        variant: u.variant,
        quantity: u.quantity,
        tags: u.tags,
      }));

      sessionStorage.setItem(storageKey, JSON.stringify(upsellData));
    } catch (error) {
      console.warn("Failed to save upsell selection:", error);
    }
  }

  /**
   * Restore complete upsell data from storage
   * Use blockId for unique storage key (Desktop vs Mobile)
   */
  restoreUpsellSelection() {
    try {
      const storageKey = `bundle-summary-${this.config.blockId}`;
      const stored = sessionStorage.getItem(storageKey);

      if (!stored) return;

      const upsellData = JSON.parse(stored);
      const mySection = this.closest('section, [id*="shopify-section"]');

      if (!mySection) {
        console.warn("⚠️ Could not find parent section, skipping restore");
        return;
      }

      let successfullyChecked = 0;

      upsellData.forEach((upsell) => {
        if (!upsell.handle) {
          console.warn("⚠️ Upsell missing handle, skipping", upsell);
          return;
        }

        const allContainers = document.querySelectorAll(
          `label[data-product-handle="${upsell.handle}"]`
        );

        allContainers.forEach((container) => {
          const containerSection = container.closest(
            'section, [id*="shopify-section"]'
          );

          if (containerSection === mySection) {
            const checkbox = container.querySelector('input[name="upsell"]');
            if (checkbox) {
              checkbox.checked = true;
              successfullyChecked++;
            }
          }
        });
      });

      if (successfullyChecked === 0) {
        sessionStorage.removeItem(storageKey);
        return;
      }
    } catch (error) {
      console.warn("Failed to restore upsell selection:", error);
    }
  }

  /**
   * Update upsell state from DOM
   */
  updateUpsellState() {
    const selectedUpsells = [];

    const mySection = this.closest('section, [id*="shopify-section"]');

    if (!mySection) {
      console.warn("⚠️ Could not find parent section, skipping upsell update");
      return;
    }

    const allCheckedCheckboxes = document.querySelectorAll(
      'input[name="upsell"]:checked'
    );

    const checkedCheckboxes = Array.from(allCheckedCheckboxes).filter(
      (checkbox) => {
        const checkboxSection = checkbox.closest(
          'section, [id*="shopify-section"]'
        );
        return checkboxSection === mySection;
      }
    );

    checkedCheckboxes.forEach((checkbox, idx) => {
      const index = checkbox.dataset.index;
      const container =
        checkbox.closest("label") || checkbox.closest(".upsell-item");

      if (!container) {
        console.warn(`⚠️ No container found for checkbox ${idx}`);
        return;
      }

      const priceData = this.extractPrice(container);
      const variantInfo = this.extractVariantInfo(container);
      const productHandle = container.dataset?.productHandle;
      if (!productHandle) {
        console.warn(`⚠️ No product handle found for upsell ${idx}, skipping`);
        return;
      }

      const productTags = this.extractTags(container);
      const discount = this.getDiscountForTags(productTags);
      const basePrice = priceData.price;
      const discountedPrice =
        discount > 0
          ? Math.round(basePrice * (1 - discount / CONFIG.PERCENT_TO_DECIMAL))
          : basePrice;

      const upsellData = {
        handle: productHandle,
        variantId: checkbox.value,
        quantity: 1,
        title: this.extractTitle(container),
        productType: this.extractProductType(container),
        price: discountedPrice,
        originalPrice: basePrice,
        compareAtPrice: priceData.compareAtPrice,
        discount: discount,
        image: this.extractImage(container),
        variant: variantInfo,
        tags: productTags,
      };

      const hiddenInput = document.querySelector(
        `input[data-upsell-item="${index}"]`
      );
      if (hiddenInput?.value) {
        upsellData.variantId = hiddenInput.value;
      }

      selectedUpsells.push(upsellData);
    });

    this.selectedUpsells = selectedUpsells;
    this.saveUpsellSelection();
    this.updateSummary();
  }

  /**
   * Extract product tags from upsell container
   */
  extractTags(container) {
    const tagsString = container.dataset?.productTags || "";
    return tagsString ? tagsString.split(",").map((tag) => tag.trim()) : [];
  }

  /**
   * Get discount percentage for product tags
   */
  getDiscountForTags(tags) {
    if (!this.config.showFakeDiscounts) {
      return 0;
    }

    for (const tag of tags) {
      if (this.config.discountRules[tag]) {
        return this.config.discountRules[tag];
      }
    }
    return 0;
  }

  /**
   * Extract product title from upsell container
   */
  extractTitle(container) {
    const titleElement = container.querySelector(
      '.kc-text, [class*="title"], h3, h4, p'
    );
    return titleElement?.textContent?.trim() || "Product";
  }

  /**
   * Extract product type from upsell container
   */
  extractProductType(container) {
    // Try data attribute first
    const productType = container.dataset?.productType;
    if (productType) {
      return productType;
    }

    // Fallback: look for product type element
    const productTypeElement = container.querySelector("[data-product-type]");
    if (productTypeElement) {
      return productTypeElement.dataset.productType;
    }

    // Last fallback: use title
    return this.extractTitle(container);
  }

  /**
   * Safely parse a price string to float, handling DE/AT/CH/EN formats
   * "1.090"     → 1090   (German thousands, no decimals)
   * "1.090,00"  → 1090   (German full)
   * "1,090.00"  → 1090   (English/CH)
   * "1'090.00"  → 1090   (Swiss alt)
   * "10,90"     → 10.90  (German decimals only)
   */
  _parsePriceString(numStr) {
    if (!numStr || !/\d/.test(numStr)) return 0;

    const str = numStr.trim();
    const lastComma = str.lastIndexOf(",");
    const lastDot   = str.lastIndexOf(".");

    let cleanStr;

    if (lastComma > lastDot) {
      const afterComma = str.substring(lastComma + 1).replace(/\s/g, "");
      if (afterComma.length === 3) {
        // "1,000" → CH/EN thousands separator (3 digits after comma)
        cleanStr = str.replace(/[\s',]/g, "");
      } else {
        // "10,90" or "1.090,00" → decimal comma
        cleanStr = str.replace(/[\s'.]/g, "").replace(",", ".");
      }
    } else if (lastDot > lastComma) {
      const afterDot = str.substring(lastDot + 1);
      const hasComma = lastComma !== -1;

      if (afterDot.length === 3 && !hasComma) {
        // "1.090" — 3 digits after lone dot → German thousands, no decimals
        cleanStr = str.replace(/[\s'.]/g, "");
      } else {
        // "1,090.00" or "1090.00" → dot is decimal
        cleanStr = str.replace(/[\s',]/g, "");
      }
    } else {
      // No separators: "1090"
      cleanStr = str.replace(/[\s',.]/g, "");
    }

    const parsed = parseFloat(cleanStr);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Extract price from upsell container
   */
  extractPrice(container) {
    const priceElement = container.querySelector(".price-list sale-price");

    if (priceElement?.dataset.money) {
      return {
        price: parseInt(priceElement.dataset.money),
        compareAtPrice: this.extractComparePrice(container),
      };
    }

    const salePriceText    = container.querySelector("sale-price")?.textContent;
    const comparePriceText = container.querySelector("compare-at-price")?.textContent;

    if (salePriceText) {
      const priceMatch = salePriceText.match(/[\d.,'\s]+/g);
      if (priceMatch) {
        const numStr = priceMatch.find((s) => /\d/.test(s))?.trim() || "";
        if (!numStr) return { price: 0, compareAtPrice: null };

        const priceInCents = Math.round(this._parsePriceString(numStr) * CONFIG.CENTS_PER_EURO);

        let compareInCents = null;
        if (comparePriceText) {
          const compareMatch = comparePriceText.match(/[\d.,'\s]+/g);
          if (compareMatch) {
            const compareNumStr = compareMatch.find((s) => /\d/.test(s))?.trim() || "";
            if (compareNumStr) {
              compareInCents = Math.round(this._parsePriceString(compareNumStr) * CONFIG.CENTS_PER_EURO);
            }
          }
        }

        return { price: priceInCents, compareAtPrice: compareInCents };
      }
    }

    return { price: 0, compareAtPrice: null };
  }

  /**
   * Extract compare at price from upsell container
   */
  extractComparePrice(container) {
    const compareElement = container.querySelector(
      ".price-list compare-at-price"
    );
    if (compareElement && compareElement.dataset.money) {
      return parseInt(compareElement.dataset.money);
    }
    return null;
  }

  /**
   * Extract image src from upsell container
   */
  extractImage(container) {
    const imgElement = container.querySelector("img");
    return imgElement?.src || imgElement?.dataset?.src || "";
  }

  /**
   * Extract variant info from upsell container
   */
  extractVariantInfo(container) {
    const currentVariantOptions = container.dataset?.currentVariantOptions;
    if (currentVariantOptions) {
      return currentVariantOptions;
    }

    const currentOptions = container.dataset?.currentOptions;
    if (currentOptions && currentOptions !== "default") {
      return this.formatOptionsFromHandle(currentOptions);
    }

    const variantTitleValues = container.querySelectorAll(
      ".variant-list-title-value"
    );
    if (variantTitleValues.length > 0) {
      const options = Array.from(variantTitleValues)
        .map((el) => el.textContent?.trim())
        .filter(Boolean);
      if (options.length > 0) {
        return options.join(" / ");
      }
    }

    const variantDisplay = container.querySelector('[class*="variant"]');
    if (variantDisplay) {
      const text = variantDisplay.textContent?.trim();
      if (text && text.length < CONFIG.MAX_VARIANT_TEXT_LENGTH) {
        return text;
      }
    }

    return "";
  }

  /**
   * Format option handles to display format
   * e.g., "hellgrau|180x200" -> "Hellgrau / 180x200"
   */
  formatOptionsFromHandle(handleString) {
    if (!handleString || handleString === "default") return "";

    return handleString
      .split("|")
      .map((option) => {
        return option
          .replace(/-/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .join(" / ");
  }

  /**
   * Calculate summary with discount
   */
  calculateSummary() {
    const mainData = this.getMainProductData();
    const mainPrice = mainData.variant.price;
    const mainCompareAtPrice = mainData.variant.compareAtPrice || mainPrice;

    const upsellsTotal = this.selectedUpsells.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const upsellsCompareTotal = this.selectedUpsells.reduce(
      (sum, item) => sum + (item.compareAtPrice || item.price) * item.quantity,
      0
    );

    const subtotal = mainPrice + upsellsTotal;
    const compareSubtotal = mainCompareAtPrice + upsellsCompareTotal;
    const upsellCount = this.selectedUpsells.length;

    const total = subtotal;
    const totalSavings = compareSubtotal - subtotal;
    const savingsPercent =
      compareSubtotal > 0
        ? Math.round(
            (totalSavings / compareSubtotal) * CONFIG.PERCENT_TO_DECIMAL
          )
        : 0;

    return {
      mainPrice,
      mainCompareAtPrice,
      upsellsTotal,
      subtotal,
      compareSubtotal,
      total,
      totalSavings,
      savingsPercent,
      itemCount: 1 + upsellCount,
      hasDiscount: totalSavings > 0,
    };
  }

  /**
   * Update summary display
   */
  updateSummary() {
    const summary = this.calculateSummary();

    this.hasRendered = true;

    if (summary.itemCount === 1 || !summary.hasDiscount) {
      this.renderEmptyState();
      return;
    }

    this.renderSummary(summary);
  }

  /**
   * Render empty state (hide block)
   */
  renderEmptyState() {
    this.classList.add("bundle-summary--hidden");
    this.innerHTML = "";
  }

  /**
   * Render full summary
   */
  renderSummary(summary) {
    this.classList.remove("bundle-summary--hidden");

    if (this.config.styleVariant === "compact") {
      this.innerHTML = this.renderCompactVariant(summary);
    } else {
      this.innerHTML = this.renderDefaultVariant(summary);
    }
  }

  /**
   * Render default variant (vertical with images and total bar)
   */
  renderDefaultVariant(summary) {
    const itemsHTML = this.config.showItemList ? this.renderItems() : "";

    return `      
      <div class="bundle-summary__total-bar">
        <div class="bundle-summary__total-label">
          <span>${this.config.title.toUpperCase()}</span>
          <span class="bundle-summary__total-discount">(-${this.formatMoney(
            summary.totalSavings
          )})</span>
        </div>
        <div class="bundle-summary__total-prices">
          <span class="bundle-summary__total-price-compare">${this.formatMoney(
            summary.compareSubtotal
          )}</span>
          <span class="bundle-summary__total-price-sale">${this.formatMoney(
            summary.total
          )}</span>
        </div>
      </div>

      ${itemsHTML}
    `;
  }

  /**
   * Render compact variant (horizontal formula without images)
   */
  renderCompactVariant(summary) {
    const items = this.getItemsForCompact();

    return `
      ${
        this.config.badgeText
          ? `<div class="bundle-summary__badge">${this.config.badgeText.toUpperCase()}</div>`
          : ""
      }
      <div class="bundle-summary__content">
        <div class="bundle-summary__items">
          ${items
            .map(
              (item, index) => `
            <div class="bundle-summary__item">
              <div class="bundle-summary__item-details">
                <div class="bundle-summary__item-title">${item.title}</div>
                ${
                  item.variant && item.variant.trim()
                    ? `<div class="bundle-summary__item-variant">${item.variant}</div>`
                    : ""
                }
              </div>
              ${
                this.config.showItemPrices
                  ? `<div class="bundle-summary__item-prices">
                ${
                  item.compareAtPrice && item.compareAtPrice > item.price
                    ? `<div class="bundle-summary__item-price-compare">${this.formatMoney(
                        item.compareAtPrice
                      )}</div>`
                    : ""
                }
                <div class="bundle-summary__item-price-sale">${this.formatMoney(
                  item.price
                )}</div>
              </div>`
                  : ""
              }
            </div>
            ${
              index < items.length - 1
                ? '<span class="bundle-summary__plus">+</span>'
                : ""
            }
          `
            )
            .join("")}
          <div class="bundle-summary__total-wrapper">
            <span class="bundle-summary__equals">=</span>
            <div class="bundle-summary__total-section">
              ${
                summary.compareSubtotal > summary.total
                  ? `<div class="bundle-summary__total-price-compare">${this.formatMoney(
                      summary.compareSubtotal
                    )}</div>`
                  : ""
              }
              <div class="bundle-summary__total-price-sale">${this.formatMoney(
                summary.total
              )}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get items array for compact variant
   */
  getItemsForCompact() {
    const items = [];
    const mainData = this.getMainProductData();

    items.push({
      title: mainData.customTitle,
      price: mainData.variant.price,
      compareAtPrice: mainData.variant.compareAtPrice,
      variant: mainData.variant.options,
      isMain: true,
    });

    items.push(
      ...this.selectedUpsells.map((upsell) => ({
        title: upsell.productType || upsell.title,
        price: upsell.price,
        compareAtPrice: upsell.compareAtPrice || null,
        variant: upsell.variant,
        isMain: false,
      }))
    );

    return items;
  }

  /**
   * Render item list
   */
  renderItems() {
    const items = [];
    const mainData = this.getMainProductData();

    items.push({
      title: mainData.title,
      image: mainData.featured_image,
      price: mainData.variant.price,
      compareAtPrice: mainData.variant.compareAtPrice,
      variant: mainData.variant.options,
      isMain: true,
    });

    items.push(
      ...this.selectedUpsells.map((upsell) => ({
        title: upsell.title,
        image: upsell.image,
        price: upsell.price,
        compareAtPrice: upsell.compareAtPrice || null,
        variant: upsell.variant,
        isMain: false,
      }))
    );

    return `
      <div class="bundle-summary__items">
        ${items
          .map(
            (item) => `
          <div class="bundle-summary__item">
            <div class="bundle-summary__item-left">
              ${
                item.image
                  ? `
                <img 
                  src="${item.image}" 
                  alt="${item.title}"
                  class="bundle-summary__item-image"
                  loading="lazy"
                >
              `
                  : ""
              }
              <div class="bundle-summary__item-details">
                <div class="bundle-summary__item-title">${item.title}</div>
                ${
                  item.variant
                    ? `<div class="bundle-summary__item-variant">${item.variant}</div>`
                    : ""
                }
              </div>
            </div>
            ${
              this.config.showItemPrices
                ? `<div class="bundle-summary__item-prices">
              ${
                item.compareAtPrice && item.compareAtPrice > item.price
                  ? `<div class="bundle-summary__item-price-compare text-subdued">${this.formatMoney(
                      item.compareAtPrice
                    )}</div>`
                  : ""
              }
              <div class="bundle-summary__item-price-sale">${this.formatMoney(
                item.price
              )}</div>
            </div>`
                : ""
            }
          </div>
        `
          )
          .join("")}
      </div>
    `;
  }

  /**
   * Format money using Shopify's money format
   * Supports all Shopify money format placeholders
   */
  formatMoney(priceInCents) {
    if (typeof priceInCents !== "number") return "€0,00";

    const amount = priceInCents / 100;
    const amountWithDot = amount.toFixed(2);
    const amountNoDecimals = Math.floor(amount).toString();

    const formatted = amountWithDot.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const moneyFormat =
      window.Shopify?.theme?.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat;

    if (moneyFormat) {
      return moneyFormat
        .replace("{{amount_with_comma_separator}}", formatted)
        .replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimals)
        .replace("{{amount_with_space_separator}}", formatted)
        .replace("{{amount_with_apostrophe_separator}}", formatted)
        .replace("{{amount_no_decimals}}", amountNoDecimals)
        .replace("{{amount}}", formatted);
    }

    return `€${formatted}`;
  }
}

if (!customElements.get("kc-bundle-summary")) {
  customElements.define("kc-bundle-summary", BundleSummary);
}
